import type {
  EndpointPreviewReturnDto,
  EndpointReturnDto,
} from '@terra/shared-contracts-endpoints';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import type {
  AssetPreviewDto,
  UnverifiedAssetStatus,
} from '@terra/shared-contracts-assets';
import { type CodeGraphWithRelations } from '@terra/shared-schemas-git';

export const ASSET_TYPE_VALUES = {
  ENDPOINT: 'endpoint',
  WEBPAGE: 'webpage',
  AGENT: 'agent',
} as const;
export type AssetDrawerType =
  (typeof ASSET_TYPE_VALUES)[keyof typeof ASSET_TYPE_VALUES];
export const ASSET_TYPE_PLURAL = {
  ENDPOINTS: `${ASSET_TYPE_VALUES.ENDPOINT}s` as const,
  WEB_PAGES: `${ASSET_TYPE_VALUES.WEBPAGE}s` as const,
  AGENTS: `${ASSET_TYPE_VALUES.AGENT}s` as const,
} as const;
export type AssetType =
  (typeof ASSET_TYPE_PLURAL)[keyof typeof ASSET_TYPE_PLURAL];
export type RoleFilterType = 'multiple' | 'specific';
export type AssetCoverageFilterType = 'all' | 'reviewed' | 'unreviewed';
export const DETECTION_VIEW = {
  ALL: 'all',
  ACTIVE: 'active',
} as const;
export type DetectionView =
  (typeof DETECTION_VIEW)[keyof typeof DETECTION_VIEW];
export const ROLE_FILTER_TYPE: {
  MULTIPLE: RoleFilterType;
  SPECIFIC: RoleFilterType;
} = {
  MULTIPLE: 'multiple',
  SPECIFIC: 'specific',
};
export const ASSET_COVERAGE_FILTER: {
  ALL: AssetCoverageFilterType;
  REVIEWED: AssetCoverageFilterType;
  UNREVIEWED: AssetCoverageFilterType;
} = {
  ALL: 'all',
  REVIEWED: 'reviewed',
  UNREVIEWED: 'unreviewed',
};
export const ASSET_VERIFIED = {
  YES: 'yes',
  NO: 'no',
} as const;
export type AssetVerified =
  (typeof ASSET_VERIFIED)[keyof typeof ASSET_VERIFIED];
export const ASSET_SEARCH_SOURCE = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
} as const;
export type AssetSearchSource =
  (typeof ASSET_SEARCH_SOURCE)[keyof typeof ASSET_SEARCH_SOURCE];
export type ApplicationEndpointListItem =
  | {
      source: typeof ASSET_SEARCH_SOURCE.VERIFIED;
      endpoint: EndpointPreviewReturnDto;
    }
  | {
      source: typeof ASSET_SEARCH_SOURCE.UNVERIFIED;
      endpoint: EndpointPreviewReturnDto;
      unverifiedStatus: UnverifiedAssetStatus;
    };
export type AssetFiltersType = {
  type: AssetType;
  methods: string[];
  roles: string[];
  roleFilterType: RoleFilterType;
  coverage?: AssetCoverageFilterType;
  showMissingCode: boolean;
  verified: AssetVerified;
  unverifiedStatuses?: UnverifiedAssetStatus[];
  endpointSecurityType?: 'vulnerable' | 'sensitive' | 'clear';
  detectionView?: DetectionView;
  url?: string;
};
type WebPageAssetPreview = Extract<AssetPreviewDto, { assetType: 'webpage' }>;

export type AssetDrawerData = {
  type: AssetDrawerType;
  data: EndpointReturnDto | WebPageWithSignedScreenshotReturnDto;
};
export type AssetDrawerDataPreview = {
  type: AssetDrawerType;
  data:
    | EndpointPreviewReturnDto
    | WebPageWithSignedScreenshotReturnDto
    | WebPageAssetPreview;
};
export const isEndpointAsset = (
  asset: AssetDrawerData | AssetDrawerDataPreview,
): asset is
  | (AssetDrawerData & { data: EndpointReturnDto })
  | (AssetDrawerDataPreview & { data: EndpointPreviewReturnDto }) =>
  asset.type === ASSET_TYPE_VALUES.ENDPOINT ||
  asset.type === ASSET_TYPE_VALUES.AGENT;
export type Technology = NonNullable<
  CodeGraphWithRelations['technologies']
>[number];
